import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { HorizontalBar } from 'react-chartjs-2';
import { Translate, I18n } from 'react-redux-i18n';
import * as actions from 'redux/actions/reportActions';
import { getReportChartOptions } from '../../reportChartOptions';

const PROFIT_COLOR = '#22c55e';
const LOSS_COLOR = '#ef4444';

function monthLabel(m) {
  const labels = [
    '', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
  ];
  return labels[Number(m)] || m;
}

function formatSectorLabel(sector) {
  if (!sector || sector === 'Unknown') {
    return I18n.t('report_sector_unknown');
  }
  return sector;
}

function sumPl(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return 0;
  }
  return items.reduce((sum, item) => sum + (Number(item.pl) || 0), 0);
}

const SectorPlChart = ({
  year,
  month,
  user_id,
  token,
  list,
  loading,
  loadList,
}) => {
  useEffect(() => {
    if (!user_id) {
      return;
    }
    loadList(user_id, token, year, month);
  }, [user_id, token, year, month, loadList]);

  const sorted = useMemo(() => {
    return [...(list || [])].sort((a, b) => (Number(b.pl) || 0) - (Number(a.pl) || 0));
  }, [list]);

  const chartHeight = Math.max(280, sorted.length * 34 + 48);

  const chartData = useMemo(() => {
    const labels = sorted.map((item) => formatSectorLabel(item.sector));
    const values = sorted.map((item) => Math.round((Number(item.pl) || 0) * 100) / 100);
    const colors = values.map((value) => (value >= 0 ? PROFIT_COLOR : LOSS_COLOR));

    return {
      labels,
      datasets: [
        {
          label: 'P/L',
          data: values,
          backgroundColor: colors,
          borderWidth: 0,
        },
      ],
    };
  }, [sorted]);

  const options = useMemo(() => {
    const base = getReportChartOptions();
    return {
      ...base,
      legend: { display: false },
      scales: {
        xAxes: [
          {
            ticks: {
              fontColor: '#94a3b8',
              beginAtZero: true,
              callback: (value) => `$${Number(value).toLocaleString()}`,
            },
            gridLines: {
              color: 'rgba(255, 255, 255, 0.06)',
              drawBorder: false,
              zeroLineColor: 'rgba(255, 255, 255, 0.18)',
            },
          },
        ],
        yAxes: [
          {
            barThickness: 18,
            maxBarThickness: 22,
            ticks: {
              fontColor: '#cbd5e1',
              fontSize: 11,
            },
            gridLines: {
              display: false,
              drawBorder: false,
            },
          },
        ],
      },
      tooltips: {
        ...base.tooltips,
        callbacks: {
          label(tooltipItem, data) {
            const value = Number(data.datasets[0].data[tooltipItem.index]) || 0;
            const row = sorted[tooltipItem.index];
            const trades = row?.count || 0;
            return `$${value.toFixed(2)} · ${trades} ${I18n.t('report_sector_trades')}`;
          },
        },
      },
    };
  }, [sorted]);

  const meta = month
    ? I18n.t('report_sector_chart_month', { year, month: monthLabel(month) })
    : I18n.t('report_sector_chart_year', { year });

  const totalPl = sumPl(sorted);

  return (
    <div className="report-panel">
      <div className="report-panel-header">
        <h3 className="report-panel-title">
          <Translate value="report_sector_pl" />
          {' · '}
          <span className={totalPl >= 0 ? 'report-pl-positive' : 'report-pl-negative'}>
            ${totalPl.toFixed(2)}
          </span>
        </h3>
        <span className="report-panel-meta">{meta}</span>
      </div>
      <div className="report-panel-body">
        {loading ? (
          <div className="report-loading">
            <Translate value="report_loading" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="report-loading">
            <Translate value="report_sector_empty" />
          </div>
        ) : (
          <div className="report-chart-wrap report-chart-wrap--sector" style={{ height: chartHeight }}>
            <HorizontalBar data={chartData} options={options} />
          </div>
        )}
      </div>
    </div>
  );
};

SectorPlChart.propTypes = {
  year: PropTypes.number,
  month: PropTypes.string,
  user_id: PropTypes.string,
  token: PropTypes.string,
  list: PropTypes.array,
  loading: PropTypes.bool,
  loadList: PropTypes.func.isRequired,
};

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.token,
    list: reducer.report.listSector,
    loading: reducer.report.loadingSector,
  };
}

const mapDispatchToProps = (dispatch) => ({
  loadList: (user_id, token, year, month) =>
    dispatch(actions.loadListSector({ user_id, token, year, month })),
});

export default connect(mapStateToProps, mapDispatchToProps)(SectorPlChart);
